'use client';

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useSubscribers } from "@letterhead/core/react";
import { useTranslations } from 'next-intl';

export default function NewsletterSection() {
  const t = useTranslations('newsletter');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const subscribers = useSubscribers();

  const formSchema = z.object({
    email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('invalidEmail')),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      await subscribers.add({
        email: values.email,
        source: "newsletter_section",
      });

      // Bigin CRM Integration
      try {
        await fetch('/api/bigin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: "Newsletter",
            lastName: "Subscription",
            email: values.email,
            source: "Newsletter",
            note: "New Newsletter Subscriber",
          }),
        });
      } catch (biginErr) {
        console.error("Bigin Integration Error:", biginErr);
      }

      toast.success(t('successTitle'), {
        description: t('successDescription'),
      });

      form.reset();
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || t('errorDescription'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="w-full flex items-center py-5 mb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl mb-4">
            {t('title')}
          </h2>
          <p className="text-lg text-gray-400 mb-10 text-[18px]">
            {t('description')}
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-md mx-auto pb-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="relative space-y-0">
                      <div className="border border-[#343333] flex flex-col sm:flex-row items-stretch sm:items-center rounded-lg overflow-hidden relative z-10 transition-all focus-within:border-teal-500/50">
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t('placeholder')}
                            required
                            className="flex-1 px-4 sm:px-6 py-3 outline-none rounded-none bg-background text-foreground placeholder:text-foreground/50 border-0 h-auto text-sm sm:text-base focus-visible:ring-0 shadow-none border-transparent focus:border-transparent focus:ring-0"
                            {...field}
                          />
                        </FormControl>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-6 sm:px-8 py-3 bg-[#343333] hover:bg-[#252424] cursor-pointer text-foreground transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed h-auto rounded-none text-sm sm:text-base border-l border-[#343333] sm:border-t-0 border-t"
                        >
                          {isSubmitting ? t('subscribing') : t('subscribe')}
                        </Button>
                      </div>
                      <FormMessage className="absolute top-full mt-2 left-0 text-red-400 bg-[#3a1515] border border-red-500/30 px-3 py-1.5 rounded-md text-xs sm:text-sm animate-in fade-in slide-in-from-top-1 shadow-lg" />
                    </FormItem>
                  )}
                />
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
}
